import { useEffect, useState } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography } from '@mui/material';
import NumberField from '@/components/common/number-field';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useNotifications } from '@/hooks/use-notifications';
import {
  useConfidenceControlQuery,
  useResetConfidenceQaMutation,
  useUpdateConfidenceSettingsMutation,
  type ConfidenceSettings,
} from '@/state/queries/player/use-confidence';

type Entity = { entityType: 'Flow' | 'Space'; entityId: string };

export function ConfidenceSettingsPanel({ entity }: { entity: Entity }) {
  const t = useTranslations();
  const notification = useNotifications();
  const { data } = useConfidenceControlQuery(entity);
  const { mutate, isPending } = useUpdateConfidenceSettingsMutation();
  const resetQa = useResetConfidenceQaMutation();
  const [draft, setDraft] = useState<ConfidenceSettings | null>(null);

  useEffect(() => {
    if (data?.settings) setDraft(data.settings);
  }, [data?.settings]);

  if (!draft) return null;

  const save = (next: ConfidenceSettings) => {
    setDraft(next);
    mutate(
      { ...entity, ...next },
      {
        onError: () => {
          if (data?.settings) setDraft(data.settings);
          notification.notifyError(t('flows.detail.control.confidence.actionError' as TranslationKey));
        },
      },
    );
  };

  return (
    <Stack spacing={1.5} sx={{ pt: 0.5 }}>
      <Typography variant="subtitle2">
        {t('flows.detail.configuration.stage.speakerMonitor.settings' as TranslationKey)}
      </Typography>
      <FormControl size="small" fullWidth>
        <InputLabel>{t('flows.detail.configuration.stage.speakerMonitor.timerBasis' as TranslationKey)}</InputLabel>
        <Select
          label={t('flows.detail.configuration.stage.speakerMonitor.timerBasis' as TranslationKey)}
          value={draft.timerBasis}
          disabled={isPending}
          onChange={(event) => save({ ...draft, timerBasis: event.target.value as ConfidenceSettings['timerBasis'] })}
        >
          <MenuItem value="actualStart">
            {t('flows.detail.configuration.stage.speakerMonitor.basisActual' as TranslationKey)}
          </MenuItem>
          <MenuItem value="plannedWallClock">
            {t('flows.detail.configuration.stage.speakerMonitor.basisWall' as TranslationKey)}
          </MenuItem>
          <MenuItem value="actualStartWithDrift">
            {t('flows.detail.configuration.stage.speakerMonitor.basisDrift' as TranslationKey)}
          </MenuItem>
        </Select>
      </FormControl>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          type="number"
          label={t('flows.detail.configuration.stage.speakerMonitor.wrapUp' as TranslationKey)}
          value={draft.wrapUpMinutes}
          disabled={isPending}
          onChange={(event) => save({ ...draft, wrapUpMinutes: Number(event.target.value) })}
          slotProps={{ htmlInput: { min: 0, max: 180 } }}
        />
        <TextField
          size="small"
          type="number"
          label={t('flows.detail.configuration.stage.speakerMonitor.final' as TranslationKey)}
          value={draft.finalMinutes}
          disabled={isPending}
          onChange={(event) => save({ ...draft, finalMinutes: Number(event.target.value) })}
          slotProps={{ htmlInput: { min: 0, max: 180 } }}
        />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">
          {t('flows.detail.configuration.stage.speakerMonitor.flash' as TranslationKey)}
        </Typography>
        <Switch
          checked={draft.flashOnOvertime}
          disabled={isPending}
          onChange={(_, checked) => save({ ...draft, flashOnOvertime: checked })}
        />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">
          {t('flows.detail.configuration.stage.speakerMonitor.qa' as TranslationKey)}
        </Typography>
        <Switch
          checked={draft.qaForTalks}
          disabled={isPending}
          slotProps={{
            input: {
              'aria-label': t('flows.detail.configuration.stage.speakerMonitor.qa' as TranslationKey),
            },
          }}
          onChange={(_, checked) =>
            save({
              ...draft,
              qaForTalks: checked,
              qaMinutes: draft.qaMinutes >= 1 && draft.qaMinutes <= 30 ? draft.qaMinutes : 5,
            })
          }
        />
      </Stack>
      <NumberField
        size="small"
        label={t('flows.detail.configuration.stage.speakerMonitor.qaMinutes' as TranslationKey)}
        helperText={t('flows.detail.configuration.stage.speakerMonitor.qaRange' as TranslationKey)}
        min={1}
        max={30}
        value={draft.qaMinutes}
        disabled={!draft.qaForTalks || isPending}
        onValueChange={(value) => {
          if (value == null || value < 1 || value > 30) return;
          save({ ...draft, qaMinutes: value });
        }}
      />
      <Button
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        disabled={resetQa.isPending || Object.keys(data?.qaOverrides ?? {}).length === 0}
        onClick={() =>
          resetQa.mutate(entity, {
            onError: () => notification.notifyError(t('flows.detail.control.confidence.actionError' as TranslationKey)),
          })
        }
      >
        {t('flows.detail.configuration.stage.speakerMonitor.qaReset' as TranslationKey)}
      </Button>
      <FormControl size="small" fullWidth>
        <InputLabel>{t('flows.detail.configuration.stage.speakerMonitor.strategy' as TranslationKey)}</InputLabel>
        <Select
          label={t('flows.detail.configuration.stage.speakerMonitor.strategy' as TranslationKey)}
          value={draft.adjustmentStrategy}
          disabled={isPending}
          onChange={(event) =>
            save({ ...draft, adjustmentStrategy: event.target.value as ConfidenceSettings['adjustmentStrategy'] })
          }
        >
          <MenuItem value="sectionOnly">
            {t('flows.detail.configuration.stage.speakerMonitor.strategySection' as TranslationKey)}
          </MenuItem>
          <MenuItem value="suggest">
            {t('flows.detail.configuration.stage.speakerMonitor.strategySuggest' as TranslationKey)}
          </MenuItem>
          <MenuItem value="absorbNext">
            {t('flows.detail.configuration.stage.speakerMonitor.strategyAbsorb' as TranslationKey)}
          </MenuItem>
          <MenuItem value="spreadRemaining">
            {t('flows.detail.configuration.stage.speakerMonitor.strategySpread' as TranslationKey)}
          </MenuItem>
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">
        {t('flows.detail.configuration.stage.speakerMonitor.autoMessages' as TranslationKey)}
      </Typography>
      {(
        [
          ['wrapUp', 'autoWrap'],
          ['final', 'autoFinal'],
          ['timeUp', 'autoTimeUp'],
          ['lastSlide', 'autoLastSlide'],
        ] as const
      ).map(([key, label]) => (
        <Stack key={key} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2">
            {t(`flows.detail.configuration.stage.speakerMonitor.${label}` as TranslationKey)}
          </Typography>
          <Switch
            checked={draft.autoMessages[key]}
            disabled={isPending}
            onChange={(_, checked) => save({ ...draft, autoMessages: { ...draft.autoMessages, [key]: checked } })}
          />
        </Stack>
      ))}
    </Stack>
  );
}
